import { useCallback, useMemo, useState } from "react";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useRuns } from "../api/use-runs";
import { useSchedule } from "../api/use-schedules";
import { assignRunNumbers } from "../lib/run-numbering";
import { RunCompareCard } from "./run-compare-card";
import { RunsTable } from "./runs-table";

interface RunsTabProps {
  scheduleId: string;
}

/**
 * Local UI state for the compare card. We persist the labels alongside the ids
 * so the card header keeps reading correctly even if the underlying runs
 * collection mutates while the card is open (e.g. a fresh run lands and
 * shifts the numbering).
 */
interface CompareState {
  currentRunId: string;
  priorRunId: string;
  currentLabel: string;
  priorLabel: string;
}

/**
 * Schedule Home → Runs tab. Fetches the runs list and renders it via
 * {@link RunsTable}, applying {@link assignRunNumbers} so the oldest run is
 * #1 and the newest is #N. The schedule query is read alongside so timestamps
 * format in the schedule's local timezone — matches the {@link SectionsTab}
 * pattern.
 *
 * Slice 6 wires the Compare action: clicking "Compare to #N-1" on a row
 * mounts a {@link RunCompareCard} above the table; clicking its Close button
 * clears the state.
 */
export function RunsTab({ scheduleId }: RunsTabProps) {
  const runsQuery = useRuns(scheduleId);
  const scheduleQuery = useSchedule(scheduleId);
  const timeZone = scheduleQuery.data?.timeZone ?? "UTC";

  const numberedRuns = useMemo(
    () => assignRunNumbers(runsQuery.data ?? []),
    [runsQuery.data],
  );

  const [compareState, setCompareState] = useState<CompareState | null>(null);

  const handleCompare = useCallback(
    (currentRunId: string, priorRunId: string) => {
      const current = numberedRuns.find((r) => r.id === currentRunId);
      const prior = numberedRuns.find((r) => r.id === priorRunId);
      if (!current || !prior) return;
      setCompareState({
        currentRunId,
        priorRunId,
        currentLabel: `Run #${current.runNumber}`,
        priorLabel: `Run #${prior.runNumber}`,
      });
    },
    [numberedRuns],
  );

  const handleCloseCompare = useCallback(() => {
    setCompareState(null);
  }, []);

  if (runsQuery.isLoading || scheduleQuery.isLoading) {
    return (
      <div data-testid="runs-tab-loading" className="py-16 flex justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (runsQuery.isError) {
    return (
      <div
        data-testid="runs-tab-error"
        className="bg-surface border border-red-500/30 rounded-lg p-6 flex items-center justify-between gap-4"
      >
        <p className="text-sm text-red-400">
          {runsQuery.error instanceof Error
            ? runsQuery.error.message
            : "Failed to load runs."}
        </p>
        <button
          type="button"
          onClick={() => runsQuery.refetch()}
          data-testid="runs-tab-retry"
          className="bg-surface-2 hover:bg-surface-2/80 text-white rounded-md px-3 py-1.5 text-[13px] font-medium"
        >
          Retry
        </button>
      </div>
    );
  }

  if (numberedRuns.length === 0) {
    return (
      <div
        data-testid="runs-tab-empty"
        className="bg-surface border border-border-subtle rounded-lg p-10 text-center text-sm text-text-muted"
      >
        No runs yet. Click Run Scheduler to create the first.
      </div>
    );
  }

  return (
    <div data-testid="schedule-tab-runs-content">
      {compareState && (
        <RunCompareCard
          scheduleId={scheduleId}
          currentRunId={compareState.currentRunId}
          priorRunId={compareState.priorRunId}
          currentRunLabel={compareState.currentLabel}
          priorRunLabel={compareState.priorLabel}
          onClose={handleCloseCompare}
        />
      )}
      <RunsTable
        runs={numberedRuns}
        timeZone={timeZone}
        onCompare={handleCompare}
      />
    </div>
  );
}
